import dayjs from "dayjs";
import numeral from "numeral";
import { useEffect, useMemo } from "react";
import { useAuth, useMessage } from "@/contexts";
import {
  CoinHelper,
  DecimalHelper,
  ErrorHelper,
  StringHelper,
} from "@/helpers";
import { useAppDispatch, useAppSelector } from "@/stores";
import { UserPermission } from "@/domains/user";
import { WalletCategory } from "@/domains/wallet";
import {
  TransferCategory,
  TransferClassHelper,
  TransferHelper,
  TransferRead,
  TransferService,
  TransferWithdrawStatusText,
} from "@/domains/transfer";
import {
  operatorMemberWithdrawSelects,
  operatorMemberWithdrawThunks,
} from "./store";
// components
import { ColumnsType, TableProps } from "antd/es/table";
import { Badge, Button, Modal, Popconfirm, Table, Tag, Tooltip } from "antd";
import {
  CopyOutlined,
  CrownOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  EyeOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  SwapRightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { TableTopFilters } from "./TableTopFilters";
import "./fix.css";

export const OperatorMemberWithdrawListPage = () => {
  const auth = useAuth();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorMemberWithdrawSelects.loading);

  const queryParams = useAppSelector(operatorMemberWithdrawSelects.queryParams);

  const transfers = useAppSelector(operatorMemberWithdrawSelects.transfers);

  const summary = useMemo(() => {
    return {
      count: transfers.length,
      amount: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(Number(t.amount) || 0)
            .value() ?? 0,
        0
      ),
      result: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(Number(t.result) || 0)
            .value() ?? 0,
        0
      ),
      charge: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(numeral(t.charge).value() ?? 0)
            .value() || 0,
        0
      ),
    };
  }, [transfers]);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorMemberWithdrawThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const getRowClassName = (record: TransferRead) => {
    if (TransferHelper.isInvalid(record.status)) {
      return "fg:disabled";
    }
    return "";
  };

  const handleRecover = (transferId: string) => {
    Modal.confirm({
      title: "確定要還原交易嗎",
      icon: <ExclamationCircleFilled />,
      async onOk() {
        await TransferService.recoverWithdraw(transferId).run();
        void dispatch(operatorMemberWithdrawThunks.list(queryParams));
        void messageApi.success("交易已還原！");
      },
    });
  };

  const handleCallback = (transferId: string) => {
    Modal.confirm({
      title: "確定要重送回調嗎？",
      icon: <ExclamationCircleFilled />,
      async onOk() {
        await TransferService.callback(transferId).run();
        void messageApi.success("回調已重送！");
      },
    });
  };

  const handleShowCallbackMessage = (message: undefined | string) => {
    if (message === undefined) return;

    const payload = JSON.parse(message);
    Modal.info({
      title: "回調紀錄",
      content: (
        <div className="font:monospace">
          <div>{payload.time}</div>
          <Tag color="success" className="mt:8 font:bold font:monospace">
            請求
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">
              <span className="mr:8 text:underline">{payload.req.url}</span>
              <CopyIconButton text={payload.req.url} />
            </div>
            <div className="p:2">
              <span className="mr:8">{payload.req.body}</span>
              <CopyIconButton text={payload.req.body} />
            </div>
          </div>
          <Tag color="processing" className="mt:8 font:bold font:monospace">
            回應
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">{payload.rsp.status}</div>
            <div className="p:2">
              <span className="mr:8">{payload.rsp.body ?? "(NO DATA)"}</span>
              <CopyIconButton text={payload.rsp.body} />
            </div>
          </div>
          <Tag color="error" className="mt:8 font:bold  font:monospace ">
            錯誤
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">{payload.exp}</div>
          </div>
        </div>
      ),
      okText: "關閉",
      width: 1000,
    });
  };

  const handleShowTransactionMessage = (message: undefined | string) => {
    Modal.info({
      title: "交易紀錄",
      content: (
        <div style={{ fontFamily: "monospace", fontSize: 12 }}>{message}</div>
      ),
      okText: "關閉",
      width: 1000,
    });
  };

  const handleApprove = (transferId: string) => {
    Modal.confirm({
      title: "確定要通過此提單嗎？",
      icon: <ExclamationCircleFilled />,
      async onOk() {
        try {
          await TransferService.approve(transferId).run();
          void dispatch(operatorMemberWithdrawThunks.list(queryParams));
          void messageApi.success("提單已通過！");
        } catch (e) {
          if (ErrorHelper.isAxiosError(e)) {
            void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
          } else {
            console.log(e);
          }
        }
      },
    });
  };

  const handleReject = (transferId: string) => {
    Modal.confirm({
      title: "確定要拒絕此提單嗎？",
      icon: <ExclamationCircleFilled />,
      okType: "danger",
      async onOk() {
        await TransferService.reject(transferId).run();
        void dispatch(operatorMemberWithdrawThunks.list(queryParams));
        void messageApi.success("提單已拒絕！");
      },
    });
  };

  const columns: ColumnsType<TransferRead> = [
    {
      width: 70,
      className: "v:top",
      title: "單號",
      dataIndex: "id",
      render(_, { id }) {
        return (
          <div className="word-break:break-all">
            <span className="mr:4">{id}</span>
            <Tooltip title="已複製" trigger="click">
              <CopyOutlined
                onClick={async () => {
                  if (id) {
                    await navigator.clipboard.writeText(id);
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      width: 260,
      className: "v:top",
      title: "平台名稱 | 平台單號 | 會員資料",
      key: "order",
      render(_, { belong, order, member }) {
        return (
          <>
            <div className="word-break:break-all">
              <CrownOutlined className="mr:8" />
              {belong?.name && StringHelper.getLength(belong?.name) >= 29 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.cutString(belong?.name, 22) + "..."}
                  </span>
                  <Popconfirm
                    title="平台名稱"
                    description={belong?.name}
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (belong?.name) {
                          await navigator.clipboard.writeText(belong?.name);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {belong?.name}
                  </span>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (belong?.name) {
                          await navigator.clipboard.writeText(belong?.name);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <ShoppingOutlined className="mr:8" />
              {order?.id && order?.id?.length >= 29 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {order?.id?.slice(0, 10) +
                      "..." +
                      order?.id?.slice(
                        order?.id?.length - 11 - 1,
                        order?.id?.length
                      )}
                  </span>
                  <Popconfirm
                    title="平台單號"
                    description={
                      <>
                        <span className="inline-block w:200 mr:8 mb:8">
                          {order?.id}
                        </span>
                        <Tooltip title="已複製" trigger="click">
                          <CopyOutlined
                            onClick={async () => {
                              if (order?.id) {
                                await navigator.clipboard.writeText(order?.id);
                              }
                            }}
                          />
                        </Tooltip>
                      </>
                    }
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (order?.id) {
                          await navigator.clipboard.writeText(order?.id);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">{order?.id}</span>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (order?.id) {
                          await navigator.clipboard.writeText(order?.id);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <IdcardOutlined className="mr:8" />
              {StringHelper.getLength(`${member?.name ? member?.name : "?"}`) >=
              29 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.cutString(
                      `${member?.name ? member?.name : "?"}`,
                      22
                    ) + "..."}
                  </span>
                  <Popconfirm
                    title="會員名稱"
                    description={member?.name}
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (member?.name) {
                          await navigator.clipboard.writeText(member?.name);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.name ? member?.name : "?"}`}
                  </span>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (member?.name) {
                          await navigator.clipboard.writeText(member?.name);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <UserOutlined className="mr:8" />
              {StringHelper.getLength(
                `${member?.username ? member?.username : "?"}`
              ) >= 29 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.cutString(
                      `${member?.username ? member?.username : "?"}`,
                      22
                    ) + "..."}
                  </span>
                  <Popconfirm
                    title="會員帳號"
                    description={member?.username}
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (member?.username) {
                          await navigator.clipboard.writeText(member?.username);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.username ? member?.username : "?"}`}
                  </span>
                  <Tooltip title="已複製" trigger="click">
                    <CopyOutlined
                      onClick={async () => {
                        if (member?.username) {
                          await navigator.clipboard.writeText(member?.username);
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
          </>
        );
      },
    },
    {
      width: 340,
      className: "v:top",
      key: "address",
      title() {
        return (
          <div className="flex gap:8">
            通道 | 匯率 | 打款地址 <SwapRightOutlined /> 收款地址
          </div>
        );
      },
      render(_, record) {
        const renderEnergyTooltip = (record: TransferRead) => {
          var text = "能量: ";
          if (record.source?.energy == null) {
            text += "-";
          } else {
            if (record.source?.energy == 0) {
              text += "0";
            } else {
              text += record.source?.energy.toFixed(2);
            }
          }
          var address = null;
          switch (record.category) {
            case TransferCategory.MemberWithdraw:
              address = record.source?.address;
              break;
          }
          return (
            address && (
              <>
                <div className="m:8">
                  <span className="mr:8">{text}</span>
                  <Button color="danger" variant="solid">
                    租賃
                  </Button>
                </div>
              </>
            )
          );
        };
        const renderAddressTag = (category: number | undefined) => {
          if (category == undefined || category == null) {
            return <Tag>尚未標記</Tag>;
          }
          switch (category) {
            case WalletCategory.OperatorCollect:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="volcano" className="cursor:pointer">
                    系統歸集
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.OperatorReceive:
              return <Tag color="volcano">系統收款</Tag>;
            case WalletCategory.External:
              return <Tag color="purple">未受管理</Tag>;
            default:
              return <></>;
          }
        };
        return (
          <div>
            {record.currency}
            {parseFloat(record.rate).toFixed(0) != "1" && (
              <Badge
                className="ml:8"
                count={record.rate}
                overflowCount={999999}
                color="#53a168"
              />
            )}
            <div className="flex">
              {record.source?.address ? (
                <>
                  {record.source?.address != null &&
                  (record.source?.address.length != 34 ||
                    record.source?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.source?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.source?.address
                        )}
                      >
                        <span className="mr:4">{record.source?.address}</span>
                      </a>
                      {renderAddressTag(record.source?.category)}
                      <Tooltip title="已複製" trigger="click">
                        <CopyOutlined
                          onClick={async () => {
                            if (record.source?.address) {
                              await navigator.clipboard.writeText(
                                record.source?.address
                              );
                            }
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                </>
              ) : (
                "?"
              )}
            </div>
            <div>
              <SwapRightOutlined />
            </div>
            <div className="flex">
              {record.target?.address ? (
                <>
                  {record.target?.address != null &&
                  (record.target?.address.length != 34 ||
                    record.target?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.target?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.target?.address
                        )}
                      >
                        <span className="mr:4">{record.target?.address}</span>
                      </a>
                      {renderAddressTag(record.target?.category)}
                      <Tooltip className="mr:4" title="已複製" trigger="click">
                        <CopyOutlined
                          onClick={async () => {
                            if (record.target?.address) {
                              await navigator.clipboard.writeText(
                                record.target?.address
                              );
                            }
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    },
    {
      width: 90,
      className: "v:top t:right",
      title: "提單點數",
      key: "amount",
      sorter: () => 1,
      render(_, { currency, amount0, amount }) {
        return (
          <>
            <div className="t:right">{DecimalHelper.fixed(amount, 2)}</div>
            <div className="t:right">{currency}</div>
            {amount0 !== amount && (
              <div className="t:right fg:gray-58">
                {DecimalHelper.fixed(amount0, 2)}
              </div>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: "實付點數",
      key: "result",
      sorter: () => 1,
      render(_, { currency, result0, result }) {
        return (
          result && (
            <>
              <div className="t:right">{DecimalHelper.fixed(result, 2)}</div>
              <div className="t:right">{currency}</div>
              {result0 !== result && (
                <div className="t:right fg:gray-58">
                  {DecimalHelper.fixed(result0, 2)}
                </div>
              )}
            </>
          )
        );
      },
    },
    {
      width: 80,
      align: "right",
      className: "v:top",
      title: "通道費",
      key: "expend",
      render(_, { currency, expend }) {
        return expend ? (
          <>
            <div className="t:right">{DecimalHelper.fixed(expend, 2)}</div>
            <div className="t:right">{CoinHelper.feeUnit(currency)}</div>
          </>
        ) : (
          <></>
        );
      },
    },
    {
      width: 80,
      align: "right",
      className: "v:top",
      title: "服務費",
      key: "charge",
      render(_, { currency, charge }) {
        return (
          charge && (
            <>
              <div className="t:right">{DecimalHelper.fixed(charge, 2)}</div>
              <div className="t:right">{currency}</div>
            </>
          )
        );
      },
    },
    {
      className: "v:top",
      title: "備註",
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "申請時間",
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "檢核時間",
      key: "checked_at",
      sorter: () => 1,
      render(_, { checked_at }) {
        return checked_at ? <div>{dayjs.$ldts(checked_at)}</div> : null;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "到帳時間",
      key: "arrived_at",
      sorter: () => 1,
      render(_, { arrived_at }) {
        return arrived_at ? <div>{dayjs.$ldts(arrived_at)}</div> : null;
      },
    },
    {
      width: 145,
      className: "v:top",
      title: "狀態",
      key: "status",
      render(_, record) {
        const TransactionEyeLink = ({
          transaction,
        }: {
          transaction: string;
        }) => (
          <a
            className="ml:8"
            target="_blank"
            href={TransferHelper.getTransactionLink(transaction)}
          >
            <EyeOutlined />
          </a>
        );

        const isCheckButtonsNeeded =
          TransferHelper.isCreated(record.status) &&
          auth.hasPerm(UserPermission.allow_to_check_member_withdraw_records);
        const CheckButtons = () => (
          <div className="flex gap:8">
            <Button type="primary" onClick={() => handleApprove(record.id)}>
              通過
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => handleReject(record.id)}
            >
              拒絕
            </Button>
          </div>
        );

        const isRecoverButtonNeededForStatusIsTransedAndTransactionIsNullOver1Mins =
          TransferHelper.isTransed(record.status) &&
          record.transaction === null &&
          dayjs().subtract(1, "minute") >= dayjs(record.created_at) &&
          auth.hasPerm(
            UserPermission.allow_to_recover_member_withdraw_records_when_status_is_transed_and_transaction_is_null_over_1_mins
          );
        const isRecoverButtonNeededForStatusIsTransedAndTransactionIsNullOver3Mins =
          TransferHelper.isTransed(record.status) &&
          record.transaction === null &&
          dayjs().subtract(3, "minute") >= dayjs(record.created_at) &&
          auth.hasPerm(
            UserPermission.allow_to_recover_member_withdraw_records_when_status_is_transed_and_transaction_is_null_over_3_mins
          );
        const RecoverButton = () => (
          <div className="flex gap:8">
            <Button
              type="primary"
              style={{ backgroundColor: "#6983e0" }}
              onClick={() => handleRecover(record.id)}
            >
              還原
            </Button>
          </div>
        );

        const isTransactionMessageButtonNeeded =
          TransferHelper.isErroredWithTransactionFailure(record.status) ||
          TransferHelper.isErroredWithUnknownReason(record.status);
        const TransactionMessageButton = () => (
          <div className="flex gap:8">
            <Button
              type="primary"
              style={{ backgroundColor: "#6983e0" }}
              onClick={() => handleShowTransactionMessage(record.transaction)}
            >
              紀錄
            </Button>
          </div>
        );

        const isCallbackButtonsNeeded = TransferHelper.isArrived(record.status);

        const CallbacksButton = () => (
          <div className="flex gap:8">
            <Button type="primary" onClick={() => handleCallback(record.id)}>
              回調
            </Button>
            {record.callback && (
              <Button
                type="primary"
                style={{ backgroundColor: "#6983e0" }}
                onClick={() => handleShowCallbackMessage(record.callback)}
              >
                紀錄
              </Button>
            )}
          </div>
        );

        return (
          <>
            <div>
              <span
                className={TransferClassHelper.getStatusColor(record.status)}
              >
                {TransferWithdrawStatusText[record.status]}
              </span>
              <span className="ml:8">
                {record.transaction && (
                  <CopyIconButton
                    text={TransferHelper.getTransactionLink(record.transaction)}
                  />
                )}
              </span>
              {!TransferHelper.isErroredWithTransactionFailure(record.status) &&
                !TransferHelper.isErroredWithUnknownReason(record.status) &&
                record.transaction &&
                record.transaction.split(",").map((transaction, idx) => {
                  return (
                    <TransactionEyeLink transaction={transaction} key={idx} />
                  );
                })}
            </div>
            <div>
              {isTransactionMessageButtonNeeded && <TransactionMessageButton />}
              {isCheckButtonsNeeded && <CheckButtons />}
              {isCallbackButtonsNeeded && <CallbacksButton />}
              {(isRecoverButtonNeededForStatusIsTransedAndTransactionIsNullOver1Mins ||
                isRecoverButtonNeededForStatusIsTransedAndTransactionIsNullOver3Mins) && (
                <RecoverButton />
              )}
            </div>
          </>
        );
      },
    },
  ];

  const tableStickyProps: TableProps["sticky"] = {
    offsetHeader: -16,
    offsetSummary: 24,
    offsetScroll: 24,
    getContainer() {
      return document.body.querySelector("main") as HTMLElement;
    },
  };

  const TableSummary = () => (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row>
        <Table.Summary.Cell index={1} colSpan={3} />
        <Table.Summary.Cell index={4} align="right">
          {summary.amount.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          {summary.result.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
        <Table.Summary.Cell index={7} align="right">
          {summary.charge.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={8} colSpan={5} />
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <div className="flex flex:col">
      <TableTopFilters />

      <Table
        rowKey="id"
        tableLayout="fixed"
        className="has-summary"
        sticky={tableStickyProps}
        loading={loading}
        columns={columns}
        dataSource={transfers}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
        summary={TableSummary}
      ></Table>
    </div>
  );
};
